import React, { FormEvent, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { Helper } from '../../utils/helper';
import { getPostLoginRedirect, persistAuthToken } from '../../utils/auth';
import { CockpitDialogTitle } from './components/CockpitDialogTitle';
import { CockpitDialogActions } from './components/CockpitDialogActions';

const authMode = (process.env.REACT_APP_AUTH_MODE || 'hybrid').toLowerCase();
const isEntraOnlyMode = authMode === 'entra_only';
const azureClientId =
  process.env.REACT_APP_AZURE_CLIENT_ID ||
  '307a0ba0-f0e4-45fc-a0e4-6874f284abd1';
const azureTenantId =
  process.env.REACT_APP_AZURE_TENANT_ID ||
  'f1b6b64c-88a9-4982-b428-4a18ba79a4e6';
const hasAzureLoginConfig = Boolean(azureClientId && azureTenantId);

const actionButtonSx = {
  minHeight: 48,
  px: 3,
  color: 'var(--cockpit-action-text)',
  backgroundColor: 'var(--cockpit-action-bg)',
  borderColor: 'var(--cockpit-accent)',
  borderRadius: 'var(--cockpit-radius-pill)',
  fontFamily: 'var(--cockpit-font-body)',
  fontSize: 16,
  fontWeight: 600,
  '&:hover': {
    color: 'var(--cockpit-action-text)',
    borderColor: 'var(--cockpit-accent)',
    backgroundColor:
      'color-mix(in srgb, var(--cockpit-action-bg) 84%, var(--cockpit-surface))'
  }
};

const fieldSx = {
  minWidth: { xs: '100%', md: 290 }
};

const compactLoginFieldSx = {
  width: { xs: '100%', md: 250 },
  minWidth: { xs: '100%', md: 250 }
};

const dialogPaperSx = {
  color: 'var(--cockpit-text)',
  background: 'var(--cockpit-surface)',
  border: '1px solid var(--cockpit-border)',
  borderRadius: 'var(--cockpit-radius)',
  boxShadow: 'var(--cockpit-shadow)'
};

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regError, setRegError] = useState<string | null>(null);
  const [regSuccess, setRegSuccess] = useState<string | null>(null);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const helper = new Helper();
  const endpointLogin = `${helper.getBaseUrl}/api/login`;
  const endpointLoginAzure = `${helper.getBaseUrl}/api/login/azure`;
  const endpointRegister = `${helper.getBaseUrl}/api/register`;

  const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePassword = (value: string) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>_-]).{8,}$/.test(
      value
    );

  const handleLogin = async () => {
    const hashedPassword = await helper.hashSHA256(password);
    setErrorMessage(null);
    try {
      const response = await fetch(endpointLogin, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password: hashedPassword })
      });
      if (!response.ok) throw new Error('Login fehlgeschlagen');
      const data = await response.json();
      persistAuthToken(data.access_token, false);
      window.location.href = getPostLoginRedirect();
    } catch {
      setErrorMessage('Benutzername oder Passwort ist falsch.');
    }
  };

  const handleAzureLogin = async (token) => {
    setErrorMessage(null);
    try {
      const response = await fetch(endpointLoginAzure, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      if (!response.ok) throw new Error('Login fehlgeschlagen');
      const data = await response.json();
      persistAuthToken(data.access_token, true);
      window.location.href = getPostLoginRedirect();
    } catch {
      setErrorMessage('Benutzername oder Passwort ist falsch.');
    }
  };

  const handleRegister = async () => {
    const validEmail = validateEmail(regEmail);
    const validPassword = validatePassword(regPassword);
    setEmailError(validEmail ? '' : 'Bitte eine gültige E-Mail eingeben.');
    setPasswordError(
      validPassword
        ? ''
        : 'Mind. 8 Zeichen, Groß- & Kleinbuchstaben, Zahl & Sonderzeichen.'
    );
    if (!validEmail || !validPassword) return;

    setRegError(null);
    setRegSuccess(null);
    try {
      const response = await fetch(endpointRegister, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: regEmail,
          password: await helper.hashSHA256(regPassword)
        })
      });
      if (!response.ok) throw new Error('Registrierung fehlgeschlagen');
      setRegSuccess(
        'Registrierung abgeschlossen! Der Administrator wurde zur Validierung informiert.'
      );
      setRegEmail('');
      setRegPassword('');
    } catch {
      setRegError('Registrierung fehlgeschlagen. Bitte prüfe deine Eingaben.');
    }
  };

  const msalConfig = {
    auth: {
      clientId: azureClientId,
      authority: `https://login.microsoftonline.com/${azureTenantId}`,
      redirectUri: `${window.location.origin}/login`
    }
  };
  const pca = hasAzureLoginConfig
    ? new PublicClientApplication(msalConfig)
    : null;

  const LoginButton = () => {
    const { instance } = useMsal();
    return (
      <Button
        onClick={() => {
          instance
            .loginPopup({ scopes: ['User.Read.All'] })
            .then((response) => handleAzureLogin(response))
            .catch(() => setErrorMessage('SSO-Login fehlgeschlagen.'));
        }}
        variant="outlined"
        sx={{ ...actionButtonSx, minWidth: 250 }}
      >
        Login via&nbsp;x1F SSO
      </Button>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 5 },
        py: { xs: 4, md: 10 },
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        color: 'var(--cockpit-text)',
        background: 'var(--cockpit-canvas)'
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 1120,
          color: 'var(--cockpit-text)',
          background: 'var(--cockpit-surface-soft)',
          border: '1px solid var(--cockpit-border)',
          borderRadius: 'var(--cockpit-radius)',
          boxShadow: 'var(--cockpit-shadow)'
        }}
      >
        <Box sx={{ px: { xs: 2.5, md: 4 }, py: 2.5 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'var(--cockpit-font-heading)',
              fontSize: { xs: 25, md: 30 },
              fontWeight: 600,
              textAlign: 'center'
            }}
          >
            Herzlich willkommen
          </Typography>
        </Box>
        <Divider sx={{ borderColor: 'var(--cockpit-border)' }} />
        <Box sx={{ px: { xs: 2.5, md: 5 }, py: { xs: 4, md: 6 } }}>
          <Stack spacing={2.5} alignItems="center">
            {errorMessage && (
              <Alert severity="error" sx={{ width: '100%', maxWidth: 700 }}>
                {errorMessage}
              </Alert>
            )}
            {hasAzureLoginConfig ? (
              <MsalProvider instance={pca!}>
                <LoginButton />
              </MsalProvider>
            ) : (
              <Typography sx={{ color: 'var(--cockpit-danger)' }}>
                Azure Login ist nicht konfiguriert.
              </Typography>
            )}

            {!isEntraOnlyMode && (
              <>
                <Typography sx={{ color: 'var(--cockpit-text-muted)' }}>
                  – oder –
                </Typography>
                <Box
                  component="form"
                  onSubmit={(event: FormEvent) => {
                    event.preventDefault();
                    void handleLogin();
                  }}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5
                  }}
                >
                  <TextField
                    id="login-username"
                    name="username"
                    label="Benutzername"
                    autoComplete="username"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    size="small"
                    inputProps={{
                      'aria-label': 'Benutzername',
                      'data-testid': 'login-username'
                    }}
                    sx={compactLoginFieldSx}
                  />
                  <TextField
                    id="login-password"
                    name="password"
                    label="Passwort"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    size="small"
                    inputProps={{
                      'aria-label': 'Passwort',
                      'data-testid': 'login-password'
                    }}
                    sx={compactLoginFieldSx}
                  />
                  <Button
                    type="submit"
                    variant="outlined"
                    sx={{ ...actionButtonSx, minWidth: 250 }}
                  >
                    Einloggen
                  </Button>
                </Box>
                <Button
                  data-testid="login-register-button"
                  variant="text"
                  onClick={() => setRegisterOpen(true)}
                  sx={{
                    color: 'var(--cockpit-accent-readable)',
                    fontFamily: 'var(--cockpit-font-body)',
                    fontSize: 17,
                    '&:hover': {
                      color: 'var(--cockpit-accent-readable)',
                      background: 'transparent'
                    }
                  }}
                >
                  Noch kein Konto? Jetzt registrieren
                </Button>
              </>
            )}
          </Stack>
        </Box>
      </Card>

      <Dialog
        data-testid="registration-dialog"
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <CockpitDialogTitle
          closeTestId="registration-close"
          closeLabel="Registrierung schließen"
          onClose={() => setRegisterOpen(false)}
        >
          Registrieren
        </CockpitDialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          {regError && <Alert severity="error">{regError}</Alert>}
          {regSuccess && <Alert severity="success">{regSuccess}</Alert>}
          <TextField
            id="registration-email"
            label="E-Mail"
            fullWidth
            value={regEmail}
            onChange={(event) => setRegEmail(event.target.value)}
            error={Boolean(emailError)}
            helperText={emailError}
            inputProps={{
              'aria-label': 'E-Mail',
              'data-testid': 'registration-email'
            }}
            sx={{ ...fieldSx, mt: 1 }}
          />
          <TextField
            id="registration-password"
            label="Passwort"
            type="password"
            fullWidth
            value={regPassword}
            onChange={(event) => setRegPassword(event.target.value)}
            error={Boolean(passwordError)}
            helperText={passwordError}
            inputProps={{
              'aria-label': 'Passwort',
              'data-testid': 'registration-password'
            }}
            sx={{ ...fieldSx, mt: 2 }}
          />
        </DialogContent>
        <CockpitDialogActions
          closeTestId="registration-close"
          closeLabel="Registrierung schließen"
          onClose={() => setRegisterOpen(false)}
          sx={{ p: 2 }}
        >
          <Button
            data-testid="registration-cancel"
            onClick={() => setRegisterOpen(false)}
            sx={actionButtonSx}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleRegister}
            variant="outlined"
            sx={actionButtonSx}
          >
            Registrieren
          </Button>
        </CockpitDialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;
